import Reveal from '../components/site/Reveal';
import SectionHeader from '../components/site/SectionHeader';
import { SECURITY_PILLARS } from '../data/site';
import { Shield, Lock, Server, Eye, KeyRound, ScrollText } from 'lucide-react';
import { useHeroImages } from '../lib/useHeroImages';

const ICONS = [Shield, Eye, Lock, KeyRound, ScrollText, Server];
const DEFAULT_HERO = 'https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg?auto=compress&cs=tinysrgb&w=1600';

export default function Security() {
  const heroes = useHeroImages();
  return (
    <div data-testid="page-security">
      <section className="pt-40 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={heroes.security || DEFAULT_HERO} alt="" className="w-full h-full object-cover hero-img hero-img-strong" />
          <div className="absolute inset-0" style={{ background: 'var(--hero-overlay)' }} />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <p className="eyebrow mb-6 flex items-center gap-2"><Shield size={14} /> Bank-grade Security</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[84px] leading-[0.95] tracking-tighter text-prim max-w-5xl">
              Custody you can audit. <span className="italic text-brand-teal-light">Trust we make visible.</span>
            </h1>
            <p className="mt-8 text-sec text-lg md:text-xl max-w-3xl leading-relaxed">
              MBK Holding governs its portfolio with the same security posture as international banking institutions — engineered for sovereign clients, family offices, and regulated industries.
            </p>
          </Reveal>

          <Reveal delay={200}>
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-subtle">
              {[
                { label: 'Uptime SLA', value: '99.99%' },
                { label: 'Encryption', value: 'AES-256' },
                { label: 'SOC monitoring', value: '24/7' },
                { label: 'Frameworks', value: '6+' },
              ].map((s) => (
                <div key={s.label} className="surface p-6 md:p-8">
                  <p className="font-display text-3xl md:text-4xl text-brand-teal-light tracking-tight">{s.value}</p>
                  <p className="mt-2 text-xs uppercase tracking-[0.2em] text-muted font-mono">{s.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 border-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <SectionHeader
              eyebrow="— Trust Architecture"
              title={<>Six layers of control, continuously audited.</>}
              lede="Every layer below operates 24/7 across our holding companies and ventures — measured, monitored, and reported to executive leadership monthly."
            />
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SECURITY_PILLARS.map((p, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <Reveal key={p.code} delay={(i % 3) * 100}>
                  <div data-testid={`security-pillar-${i}`} className="border border-subtle surface-2 p-8 rounded-md card-glow h-full">
                    <div className="flex items-center justify-between">
                      <div className="w-11 h-11 rounded-md bg-brand-teal/10 border border-brand-teal/30 flex items-center justify-center">
                        <Icon size={18} className="text-brand-teal-light" />
                      </div>
                      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-brand-teal-light px-2.5 py-1 rounded border border-brand-teal/30 bg-brand-teal/5">{p.code}</span>
                    </div>
                    <h3 className="font-display text-2xl text-prim mt-6 tracking-tight">{p.title}</h3>
                    <p className="mt-3 text-sec leading-relaxed text-sm">{p.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Governance call-out */}
      <section className="py-24 border-t border-subtle bg-base">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-7">
              <p className="eyebrow mb-5">— Governance</p>
              <h2 className="font-display text-4xl md:text-5xl text-prim tracking-tighter leading-[1.05]">
                Risk, audit and intelligence — under one roof.
              </h2>
              <p className="mt-6 text-sec leading-relaxed text-lg max-w-2xl">
                <a href="https://vigilantentities.com" target="_blank" rel="noreferrer" className="text-brand-teal-light hover-line">Vigilant Entities</a> — an M³ portfolio company — is the institutional operating system for sovereign holdings. Twelve sectors, a rotating advisory council and twenty-six AI officers vet every founder, investor and venture with audit-grade discipline. Its governance, risk, audit, ESG and vetting controls are embedded across MBK ventures.
              </p>
              <ul className="mt-8 space-y-3 text-sec">
                {['Independent third-party penetration testing every quarter','Continuous vulnerability disclosure program','Hardware-token MFA for all privileged access','Quarterly board-level security reviews'].map((b) => (
                  <li key={b} className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-brand-teal-light mt-2.5" />{b}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="lg:col-span-5" delay={200}>
              <div className="relative shimmer-border p-1">
                <div className="relative surface rounded-xl p-8 font-mono text-xs text-sec leading-relaxed">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-2 h-2 rounded-full bg-red-500/70" />
                    <span className="w-2 h-2 rounded-full bg-yellow-500/70" />
                    <span className="w-2 h-2 rounded-full bg-green-500/70" />
                    <span className="ml-2 text-muted">/ mbk-soc · live</span>
                  </div>
                  <div className="space-y-1.5">
                    <p><span className="text-brand-teal-light">$</span> tls.version <span className="text-prim">→ 1.3 verified</span></p>
                    <p><span className="text-brand-teal-light">$</span> encryption.atRest <span className="text-prim">→ AES-256-GCM</span></p>
                    <p><span className="text-brand-teal-light">$</span> mfa.enforcement <span className="text-prim">→ 100% (hardware token)</span></p>
                    <p><span className="text-brand-teal-light">$</span> compliance.iso27001 <span className="text-prim">→ active</span></p>
                    <p><span className="text-brand-teal-light">$</span> compliance.soc2 <span className="text-prim">→ Type II</span></p>
                    <p><span className="text-brand-teal-light">$</span> compliance.pdppl <span className="text-prim">→ active (Law 13/2016)</span></p>
                    <p><span className="text-brand-teal-light">$</span> incidents.open <span className="text-prim">→ 0</span></p>
                    <p><span className="text-brand-teal-light">$</span> last.audit <span className="text-prim">→ 2025-11-14 · clean</span></p>
                    <p className="pt-2 text-brand-teal-light">// system nominal · custody intact</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
